import { useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import groupBy from 'lodash.groupby'
import {
  NumberParam,
  withDefault,
  useQueryParams,
  BooleanParam,
  StringParam,
} from 'use-query-params'

import { Spin, useMessage } from '@pepper/ui'
import type { Domain, Topic } from '@pepper/utils/src/types/seo'
import { pluralize } from '@pepper/utils/src/function/pluralize'

import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'
import CreateItemDropdown from 'components/CreateItemDropdown'
import ContentIdeasDrawer from 'components/content-ideas/ContentIdeasDrawer'

import CRRDrawer from '../../CRR/CRRDrawer/CRRDrawer'
import Table from './Table'
import Filter from './Filter'
import LabRouteSwitch from '../LabRouteSwitch'
import EmptyState from './EmptyState'
import EditTopicModal from './EditTopicModal'
import CreateItemModal from './CreateItemModal'
import ImportModal from './ImportModal'
import CreateTopicsDropdown from './CreateTopicsDropdown'
import AppliedFilters from './Filter/AppliedFilters'
import { ExportButton } from '../ExportButton/ExportButton'

import { useProjectTopics } from 'utils/hooks/useProjectTopics'
import { useDeleteProjectTopics } from 'utils/hooks/useDeleteProjectTopics'
import { useGenerateBulkSeoGuidelines } from 'utils/hooks/useGenerateBulkSeoGuidelines'

import s from './Topics.module.css'

type Props = {
  projectId: string
  companyId: string
  isLoadingProjectDomain: boolean
  domain?: Domain
}

export const Topics = ({
  companyId,
  projectId,
  domain,
  isLoadingProjectDomain,
}: Props) => {
  const [queryParams, setQueryParams] = useQueryParams({
    page: withDefault(NumberParam, 1),
    content_ideas: withDefault(BooleanParam, false),
    keywords_to_topics: withDefault(BooleanParam, false),
    isImportModalOpen: withDefault(BooleanParam, false),
    view_assignment: StringParam,
    crr_id: withDefault(BooleanParam, undefined),
    item_id: withDefault(BooleanParam, undefined),
    topic: withDefault(StringParam, undefined),
    keyword: withDefault(StringParam, undefined),
    sort: withDefault(StringParam, undefined),
    limit: withDefault(NumberParam, 25),
    positionGte: withDefault(NumberParam, undefined),
    positionLte: withDefault(NumberParam, undefined),
    exclude_position: withDefault(BooleanParam, undefined),
  })

  const [openItemModal, setOpenItemModal] = useState(false)
  const [itemType, setItemType] = useState<'assignment' | 'private_doc'>(
    'assignment'
  )
  const [activeTopic, setActiveTopic] = useState<Topic>()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAddPrimaryKeywordModalOpen, setIsAddPrimaryKeywordModalOpen] =
    useState(false)
  const [isCRRDrawerOpen, setIsCRRDrawerOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([])
  const [isAddingBulkTopics, setIsAddingBulkTopics] = useState(false)
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([])

  const { showError, showSuccess } = useMessage()

  const {
    data,
    isLoading: isTopicsLoading,
    refetch,
  } = useProjectTopics({
    projectId,
    companyId,
    page: queryParams.page,
    limit: queryParams.limit,
    queryParamsFilter: {
      topic: queryParams.topic?.split(','),
      keyword: queryParams.keyword?.split(','),
      existsCrrId: queryParams.crr_id,
      existsItemId: queryParams.item_id,
      sort: queryParams.sort,
      lte: queryParams.positionLte,
      gte: queryParams.positionGte,
      exclude_position: queryParams.exclude_position,
    },
    enabled: true,
  })

  const { mutateAsync: deleteTopics, isLoading: isDeleting } =
    useDeleteProjectTopics()
  const {
    mutateAsync: generateBulkSeoGuidelines,
    isLoading: isGeneratingBulkGuidelines,
  } = useGenerateBulkSeoGuidelines()

  const isFilterApplied =
    queryParams.crr_id !== undefined ||
    queryParams.item_id ||
    !!queryParams.topic?.length ||
    !!queryParams.keyword?.length ||
    queryParams.exclude_position
  const isDomainRemoved = !!domain?.deleted_at
  const isDomainNotLinked = !domain

  const handleOpenItemModal = (
    type: 'assignment' | 'private_doc',
    topics?: Topic[]
  ) => {
    const _selectedTopics =
      topics ??
      data?.records?.filter(
        topic => !topic.item_id && selectedRows.includes(topic.id)
      ) ??
      []
    setItemType(type)
    setSelectedTopics(_selectedTopics)
    setOpenItemModal(true)
  }

  const handleRowSelect = (selectedRow: React.Key[]) => {
    setSelectedRows([...selectedRow])
    setSelectedTopics(
      data?.records.filter(
        topic => !topic.item_id && selectedRow.includes(topic.id)
      ) ?? []
    )
  }

  const handleDeleteTopics = async (_topic?: Topic) => {
    const topicIds = _topic ? [_topic.id] : selectedRows.map(String)
    const { status, message } = await deleteTopics({
      companyId,
      projectId,
      topicIds,
    })
    if (status === 'success') {
      showSuccess(`${pluralize(topicIds.length, 'topic')} deleted successfully`)
      setSelectedRows([])
    } else {
      showError(message)
    }
  }

  const handleDiscoverTopics = () => {
    setQueryParams({ content_ideas: true })
  }

  const handleReportClick = (topic: Topic) => {
    const hasPrimaryKeyword = topic.keywords?.some(k => k.type === 'primary')
    setActiveTopic(topic)
    if (hasPrimaryKeyword) {
      setIsCRRDrawerOpen(true)
    } else {
      setIsAddPrimaryKeywordModalOpen(true)
    }
  }

  const handleEditProjectTopic = (_topic: Topic) => {
    setActiveTopic(_topic)
    setIsEditModalOpen(true)
  }

  const handleViewItemClick = (
    itemId: string,
    _itemType: 'private_doc' | 'assignment'
  ) => {
    if (_itemType === 'private_doc') {
      window.open(`/${companyId}/items/${itemId}`, '_blank')
    } else {
      setQueryParams({ view_assignment: itemId })
    }
  }

  const handleBulkGuidelineGeneration = async () => {
    const response = await generateBulkSeoGuidelines({
      companyId,
      topics: topicsWithoutGuidelines.map(topic => ({
        topic_id: topic.id,
        topic: topic.name,
        region_code: domain?.region_code,
      })),
    })
    if (response.status === 'error') {
      showError(response.error)
    } else {
      showSuccess(`You're on the right path! Report generation in progress...`)
      setSelectedRows([])
      refetch()
    }
  }

  const topicsWithoutGuidelines = useMemo(() => {
    const _selectedTopics =
      data?.records.filter(topic => selectedRows.includes(topic.id)) ?? []
    return _selectedTopics.filter(
      topic => !topic.crr_id && topic.keywords?.some(k => k.type === 'primary')
    )
  }, [selectedRows.length])

  const secondaryKeywords = groupBy(
    data?.records.find(topic => topic.id === activeTopic?.id)?.keywords,
    'type'
  )

  const crrIncludedTopic = data?.records.find(
    record => record.id === activeTopic?.id
  )

  return (
    <Spin spinning={isAddingBulkTopics || isLoadingProjectDomain}>
      <div className={s.wrapper}>
        <div className={s.header}>
          <LabRouteSwitch />
          <div className={s.filterWrapper}>
            {!data?.total_records ? null : (
              <ExportButton
                title="Export Topics"
                isSelection={selectedRows.length > 0}
                selectedTopics={data?.records.filter(topic =>
                  selectedRows.includes(topic.id)
                )}
                type="topic"
                projectId={projectId}
                companyId={companyId}
              />
            )}

            {(!!data?.total_records || isFilterApplied) && <Filter />}
            <CreateTopicsDropdown
              onDiscoverTopics={handleDiscoverTopics}
              onImportTopics={() => {
                setQueryParams({ isImportModalOpen: true })
              }}
            />
          </div>
        </div>
        {isFilterApplied && <AppliedFilters />}
        {((!isTopicsLoading && data?.total_records === 0 && !isFilterApplied) ||
          (isDomainNotLinked && !isLoadingProjectDomain)) && (
          <div className={s.emptyList}>
            <EmptyState
              isDomainNotLinked={isDomainNotLinked}
              isDomainRemoved={isDomainRemoved}
              onTopicResearch={handleDiscoverTopics}
              onImport={() => {
                setQueryParams({ isImportModalOpen: true })
              }}
            />
          </div>
        )}

        {!isTopicsLoading && data?.total_records === 0 && isFilterApplied && (
          <div className={s.emptyList}>No topics found</div>
        )}

        {data?.total_records !== 0 && !isDomainNotLinked && (
          <div className={s.body}>
            <Table
              dataSource={data?.records}
              loading={isTopicsLoading}
              totalRecords={data?.total_records ?? 0}
              onSelect={handleRowSelect}
              onPageChange={page => {
                setQueryParams({ page })
                setSelectedTopics([])
                setSelectedRows([])
              }}
              onReportClick={handleReportClick}
              rowsSelected={selectedRows}
              onDeleteTopic={handleDeleteTopics}
              onEditTopic={handleEditProjectTopic}
              onCreateReport={handleReportClick}
              onViewItemClick={handleViewItemClick}
              page={queryParams.page}
              total={data?.total_records ?? 0}
              limit={queryParams.limit}
              projectId={projectId}
              onCreateOrderClick={topic =>
                handleOpenItemModal('assignment', [topic])
              }
              onCreatePrivateDocClick={topic =>
                handleOpenItemModal('private_doc', [topic])
              }
              onPageSizeChange={pageSize =>
                setQueryParams({
                  limit: pageSize,
                  page: 1,
                })
              }
              isDomainRemoved={isDomainRemoved}
            />

            <BottomPopup
              selectedCount={selectedRows.length}
              onClose={() => {
                setSelectedRows([])
              }}
            >
              {selectedTopics.length > 0 && (
                <CreateItemDropdown
                  onCreateOrderClick={() => handleOpenItemModal('assignment')}
                  onCreatePrivateDocClick={() =>
                    handleOpenItemModal('private_doc')
                  }
                >
                  <BottomPopupBtn icon={<Icon icon="lucide:plus" />}>
                    Create {pluralize(selectedTopics.length, 'Item')}
                  </BottomPopupBtn>
                </CreateItemDropdown>
              )}
              {topicsWithoutGuidelines.length > 0 && (
                <BottomPopupBtn
                  icon={<Icon icon="lucide:plus" />}
                  onClick={handleBulkGuidelineGeneration}
                  isLoading={isGeneratingBulkGuidelines}
                >
                  Create
                  {` ${pluralize(
                    topicsWithoutGuidelines.length,
                    'SEO Guideline'
                  )}`}
                </BottomPopupBtn>
              )}
              <BottomPopupBtn
                type="delete"
                onClick={() => handleDeleteTopics()}
                isLoading={isDeleting}
              />
            </BottomPopup>
          </div>
        )}
      </div>
      {!!domain && (
        <ContentIdeasDrawer
          domain={domain}
          companyId={companyId}
          projectId={projectId}
        />
      )}
      <CRRDrawer
        open={isCRRDrawerOpen}
        topic={crrIncludedTopic}
        onClose={() => {
          setIsCRRDrawerOpen(false)
          setActiveTopic(undefined)
        }}
        onCreateOrderClick={topic => handleOpenItemModal('assignment', [topic])}
        onCreatePrivateDocClick={topic =>
          handleOpenItemModal('private_doc', [topic])
        }
        secondaryKeywords={secondaryKeywords.secondary}
      />
      {!!domain && (
        <EditTopicModal
          open={isEditModalOpen || isAddPrimaryKeywordModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setActiveTopic(undefined)
            setIsAddPrimaryKeywordModalOpen(false)
          }}
          domainRegion={domain?.region_code}
          companyId={companyId}
          projectId={projectId}
          selectedProjectTopic={activeTopic}
          {...(isAddPrimaryKeywordModalOpen && {
            title: 'Add Keywords to create SEO Guidelines',
            showAttachSeoGuidelines: true,
          })}
        />
      )}
      <CreateItemModal
        selectedTopics={selectedTopics}
        itemType={itemType}
        open={openItemModal}
        onClose={() => {
          setSelectedRows([])
          setSelectedTopics([])
          setOpenItemModal(false)
          refetch()
        }}
        onSuccess={() => setIsCRRDrawerOpen(false)}
      />
      {!!domain && (
        <ImportModal
          projectId={projectId}
          companyId={companyId}
          domainRegion={domain.region_code}
          open={!!queryParams.isImportModalOpen}
          setIsAddingBulkTopics={setIsAddingBulkTopics}
          onClose={() => setQueryParams({ isImportModalOpen: undefined })}
        />
      )}
    </Spin>
  )
}
